import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
    SparklesIcon,
    PhotoIcon,
    PlusIcon,
    TrashIcon,
    PencilIcon,
    Bars3BottomLeftIcon,
    CloudArrowUpIcon
} from '@heroicons/react/24/outline';
import ConfirmDialog from '../components/ConfirmDialog';

const GalleryCelebrationImagesPage = () => {
    const [settings, setSettings] = useState({
        section_title: '',
        section_description: ''
    });
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);

    // Image Form State
    const [currentImage, setCurrentImage] = useState({
        image_url: '',
        alt_text: '',
        caption: '',
        display_order: 0,
        is_active: true
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    // Delete confirmation state
    const [deleteImageConfirm, setDeleteImageConfirm] = useState({ isOpen: false, id: null });

    const fetchData = async () => {
        try {
            setLoading(true);
            const [settingsRes, imagesRes] = await Promise.all([
                axios.get('/api/admin/gallery/celebration/settings'),
                axios.get('/api/admin/gallery/celebration/images')
            ]);

            if (settingsRes.data.success) setSettings(settingsRes.data.data);
            if (imagesRes.data.success) setImages(imagesRes.data.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load celebration images data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSaveSettings = async () => {
        try {
            setSaving(true);
            await axios.put('/api/admin/gallery/celebration/settings', settings);
            toast.success('Settings updated successfully');
        } catch (error) {
            toast.error('Failed to update settings');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveImage = async () => {
        try {
            const submitData = new FormData();
            submitData.append('caption', currentImage.caption);
            submitData.append('alt_text', currentImage.alt_text);
            submitData.append('display_order', currentImage.display_order);
            submitData.append('is_active', currentImage.is_active ? '1' : '0');

            if (imageFile) {
                submitData.append('image', imageFile);
            } else {
                submitData.append('image_url', currentImage.image_url);
            }

            if (currentImage.id) {
                await axios.put(`/api/admin/gallery/celebration/images/${currentImage.id}`, submitData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Image updated');
            } else {
                await axios.post('/api/admin/gallery/celebration/images', submitData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Image added');
            }
            setShowImageModal(false);
            setImageFile(null);
            setImagePreview(null);
            fetchData();
        } catch (error) {
            console.error('Error saving image:', error);
            toast.error(error.response?.data?.message || 'Failed to save image');
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.match(/^image\/(webp|png|jpeg)$/)) {
                toast.error('Only WebP, PNG and JPEG formats are allowed');
                e.target.value = '';
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                toast.error('File size must be less than 5MB');
                e.target.value = '';
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setImageFile(file);
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDeleteImage = async () => {
        try {
            await axios.delete(`/api/admin/gallery/celebration/images/${deleteImageConfirm.id}`);
            toast.success('Image deleted');
            setDeleteImageConfirm({ isOpen: false, id: null });
            fetchData();
        } catch (error) {
            toast.error('Failed to delete image');
        }
    };

    return (
        <div className="space-y-8 pb-20">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-slate-800">"We Celebrate Together" - Management</h1>
                <p className="text-slate-500 mt-1">Manage section content and celebration images for the Gallery page</p>
            </div>

            {loading ? (
                <div className="animate-pulse space-y-4">
                    <div className="h-64 bg-slate-100 rounded-2xl"></div>
                    <div className="h-64 bg-slate-100 rounded-2xl"></div>
                </div>
            ) : (
                <>
                    {/* General Settings */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
                        <h2 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                            <Bars3BottomLeftIcon className="h-5 w-5 text-primary-600" />
                            Section Content Settings
                        </h2>
                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                                <input
                                    type="text"
                                    value={settings.section_title}
                                    onChange={(e) => setSettings({ ...settings, section_title: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                                    placeholder="e.g., We Celebrate Together"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Section Description</label>
                                <textarea
                                    rows="3"
                                    value={settings.section_description}
                                    onChange={(e) => setSettings({ ...settings, section_description: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end mt-6">
                            <button
                                onClick={handleSaveSettings}
                                disabled={saving}
                                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold shadow-lg disabled:opacity-50"
                            >
                                {saving ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </div>

                    {/* Images List */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <PhotoIcon className="h-5 w-5 text-primary-600" />
                                Celebration Images & Captions
                            </h2>
                            <button
                                onClick={() => {
                                    setCurrentImage({ image_url: '', alt_text: '', caption: '', display_order: images.length, is_active: true });
                                    setImagePreview(null);
                                    setImageFile(null);
                                    setShowImageModal(true);
                                }}
                                className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-semibold"
                            >
                                <PlusIcon className="h-5 w-5" /> Add New Image
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {images.map((img) => (
                                <div key={img.id} className="border border-slate-100 rounded-2xl overflow-hidden bg-slate-50 group relative">
                                    <img src={img.image_url} alt={img.alt_text} className="w-full h-48 object-cover" />
                                    <div className="p-4">
                                        <p className="text-sm text-slate-600 italic leading-relaxed">{img.caption}</p>
                                        <div className={`mt-2 px-2 py-1 rounded text-xs font-semibold w-fit ${img.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                                            {img.is_active ? 'Active' : 'Inactive'}
                                        </div>
                                    </div>
                                    <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => {
                                            setCurrentImage(img);
                                            setImagePreview(img.image_url);
                                            setImageFile(null);
                                            setShowImageModal(true);
                                        }} className="p-2 bg-white text-blue-600 rounded-xl shadow-lg">
                                            <PencilIcon className="h-5 w-5" />
                                        </button>
                                        <button onClick={() => setDeleteImageConfirm({ isOpen: true, id: img.id })} className="p-2 bg-white text-red-600 rounded-xl shadow-lg">
                                            <TrashIcon className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {/* Image Modal */}
            {showImageModal && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl w-full max-w-xl p-8 shadow-2xl">
                        <h3 className="text-xl font-bold text-slate-800 mb-6">Manage Celebration Image</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Image Selection</label>
                                <p className="text-xs text-slate-500 mb-3">
                                    📐 Recommended: 300 x 300 pixels | Formats: WebP, PNG, JPEG only
                                </p>
                                <div className="flex gap-4">
                                    <div className="flex-1">
                                        <label className="block border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-primary-500 transition-colors">
                                            <input
                                                type="file"
                                                accept=".webp,.png,.jpeg,.jpg"
                                                onChange={handleImageChange}
                                                className="hidden"
                                            />
                                            <CloudArrowUpIcon className="h-10 w-10 text-slate-400 mx-auto mb-2" />
                                            <p className="text-sm font-medium text-slate-600">Change Image</p>
                                            <p className="text-xs text-slate-400 mt-1">WebP, PNG, JPG, max 5MB</p>
                                        </label>
                                    </div>
                                    {imagePreview && (
                                        <div className="w-32 h-24 rounded-xl overflow-hidden border border-slate-200">
                                            <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Alt Text</label>
                                <input
                                    type="text"
                                    value={currentImage.alt_text}
                                    onChange={(e) => setCurrentImage({ ...currentImage, alt_text: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                                    placeholder="e.g., Office Social"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Caption</label>
                                <textarea
                                    rows="2"
                                    value={currentImage.caption}
                                    onChange={(e) => setCurrentImage({ ...currentImage, caption: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                                    placeholder="e.g., Festival celebrations"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Display Order</label>
                                    <input
                                        type="number"
                                        value={currentImage.display_order}
                                        onChange={(e) => setCurrentImage({ ...currentImage, display_order: parseInt(e.target.value) })}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div className="flex items-end">
                                    <label className="flex items-center gap-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={currentImage.is_active}
                                            onChange={(e) => setCurrentImage({ ...currentImage, is_active: e.target.checked })}
                                            className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500 border-gray-300"
                                        />
                                        <span className="text-sm font-semibold text-slate-700">Active</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-8">
                            <button onClick={() => setShowImageModal(false)} className="px-6 py-2.5 text-slate-600 font-semibold">Cancel</button>
                            <button onClick={handleSaveImage} className="bg-primary-600 text-white px-8 py-2.5 rounded-xl font-semibold shadow-lg">Save Image</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Image Confirmation Dialog */}
            <ConfirmDialog
                isOpen={deleteImageConfirm.isOpen}
                onClose={() => setDeleteImageConfirm({ isOpen: false, id: null })}
                onConfirm={handleDeleteImage}
                title="Delete Celebration Image"
                message="Are you sure you want to delete this celebration image? This action cannot be undone."
            />
        </div>
    );
};

export default GalleryCelebrationImagesPage;
